/**
 * TaskLibrary — Biblioteca de tareas (admin only).
 *
 * Allows the Director Deportivo to create, upload, edit and delete task
 * templates that coaches reuse across teams. Each task can include an image
 * (drawn on the Pizarra or uploaded directly), description, objective and tags.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import YouTubeThumbnail from "@/components/YouTubeThumbnail";
import TacticalBoard from "@/components/TacticalBoard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BookOpen, Plus, Pencil, Trash2, Image as ImageIcon, Upload, Tag, Save, X,
  Search, Share2, Youtube, Target, Maximize2, Play, Film,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_EXT = ["jpg", "jpeg", "png", "webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export default function TaskLibrary() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | {...existing}
  const [viewing, setViewing] = useState(null); // task shown in the detail dialog
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");

  const isAdmin = user?.role === "admin";
  const canCreate = isAdmin || user?.role === "coach";
  const [confirmDelete, setConfirmDelete] = useState(null);

  const refresh = () => {
    setLoading(true);
    Promise.all([api.get("/task-templates"), api.get("/teams")])
      .then(([r1, r2]) => {
        setTemplates(r1.data || []);
        setTeams(r2.data || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const tagOptions = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => (t.tags || []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (tagFilter && !(t.tags || []).includes(tagFilter)) return false;
      if (q && !(`${t.title} ${t.description} ${t.objective}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [templates, search, tagFilter]);

  const deleteOne = async (id) => {
    try {
      await api.delete(`/task-templates/${id}`);
      toast.success("Tarea borrada");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Nunito" }}>
            <BookOpen className="h-7 w-7 text-purple-600" /> Biblioteca de Tareas
          </h1>
          <p className="text-sm text-slate-500">
            Crea y comparte tareas con todos los entrenadores del club. Las verán en sus sesiones.
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setEditing({})}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
            data-testid="task-library-new"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Nueva tarea
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título, descripción u objetivo…"
            className="pl-9"
            data-testid="task-library-search"
          />
        </div>
        <Select value={tagFilter || "__all__"} onValueChange={(v) => setTagFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="task-library-tag-filter">
            <SelectValue placeholder="Filtrar por etiqueta" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas las etiquetas</SelectItem>
            {tagOptions.map((t) => (
              <SelectItem key={t} value={t}>#{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-sm text-slate-400 py-10 text-center italic">Cargando biblioteca…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          {templates.length === 0
            ? "Aún no hay tareas en la biblioteca. Crea la primera con el botón 'Nueva tarea'."
            : "Ninguna tarea coincide con el filtro."}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <TaskCard
              key={t.id}
              template={t}
              teams={teams}
              canManage={isAdmin || t.created_by === user?.id}
              onOpen={() => setViewing(t)}
              onEdit={() => setEditing(t)}
              onDelete={() => setConfirmDelete(t)}
            />
          ))}
        </div>
      )}

      {editing !== null && (
        <TaskTemplateDialog
          template={editing}
          teams={isAdmin ? teams : teams.filter((t) => (user?.team_ids || []).includes(t.id))}
          existingTags={tagOptions}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {viewing !== null && (
        <TaskDetailDialog
          template={viewing}
          teams={teams}
          canManage={isAdmin || viewing.created_by === user?.id}
          onEdit={() => { setEditing(viewing); setViewing(null); }}
          onClose={() => setViewing(null)}
        />
      )}

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="task-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar esta tarea?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{confirmDelete?.title}" de la biblioteca. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="task-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { const id = confirmDelete?.id; setConfirmDelete(null); if (id) deleteOne(id); }}
              data-testid="task-delete-accept"
            >
              Borrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TaskCard({ template, teams, canManage, onOpen, onEdit, onDelete }) {
  const teamName = template.team_id
    ? teams.find((t) => t.id === template.team_id)?.name || "Equipo"
    : "Compartida con el club";
  const imgUrl = template.image_id
    ? `${BACKEND}/api/attachments/${template.image_id}/file`
    : null;
  const hasAnim = (template.board_data?.scenes?.length || 0) > 1;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition"
         data-testid={`task-card-${template.id}`}>
      <div
        className="bg-slate-100 aspect-[16/10] flex items-center justify-center overflow-hidden cursor-pointer relative group"
        onClick={onOpen}
        title="Ver tarea completa"
        data-testid={`task-open-${template.id}`}
      >
        {imgUrl ? (
          <img src={imgUrl} alt={template.title}
               className="object-cover w-full h-full"
               onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <ImageIcon className="h-10 w-10 text-slate-300" />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition flex items-center justify-center">
          <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition" />
        </div>
        {hasAnim && (
          <span
            className="absolute top-2 left-2 inline-flex items-center gap-1 bg-purple-600/95 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shadow"
            data-testid={`task-anim-badge-${template.id}`}
          >
            <Film className="h-3 w-3" /> Animación
          </span>
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div
            className="font-bold text-slate-800 truncate flex-1 cursor-pointer hover:text-purple-700"
            onClick={onOpen}
          >
            {template.title}
          </div>
          {canManage && (
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`task-edit-${template.id}`}>
                <Pencil className="h-4 w-4 text-slate-500" />
              </Button>
              <Button variant="ghost" size="icon" onClick={onDelete} data-testid={`task-delete-${template.id}`}>
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            </div>
          )}
        </div>
        {template.description && (
          <p className="text-xs text-slate-600 line-clamp-2">{template.description}</p>
        )}
        {template.objective && (
          <p className="text-[11px] text-purple-700 italic line-clamp-1">🎯 {template.objective}</p>
        )}
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <Share2 className="h-3 w-3" />
          <span className="truncate">{teamName}</span>
          {template.video_url && (
            <a
              href={template.video_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-rose-600 font-bold hover:text-rose-800"
              title={template.video_url}
              onClick={(e) => e.stopPropagation()}
              data-testid={`task-video-link-${template.id}`}
            >
              <Youtube className="h-3 w-3" /> Vídeo
            </a>
          )}
          {template.times_used > 0 && (
            <span className="ml-auto bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold">
              usada {template.times_used}×
            </span>
          )}
        </div>
        {(template.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {template.tags.map((tag) => (
              <span key={tag} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskDetailDialog({ template, teams, canManage, onEdit, onClose }) {
  const [showAnim, setShowAnim] = useState(false);
  const teamName = template.team_id
    ? teams.find((t) => t.id === template.team_id)?.name || "Equipo"
    : "Compartida con todo el club";
  const imgUrl = template.image_id
    ? `${BACKEND}/api/attachments/${template.image_id}/file`
    : null;
  const hasAnim = (template.board_data?.scenes?.length || 0) > 1;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black text-slate-900" data-testid="task-detail-title">
            {template.title}
          </DialogTitle>
        </DialogHeader>

        {imgUrl && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
            <img
              src={imgUrl}
              alt={template.title}
              className="w-full max-h-[62vh] object-contain"
              data-testid="task-detail-image"
            />
          </div>
        )}

        {template.objective && (
          <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 flex items-start gap-2" data-testid="task-detail-objective">
            <Target className="h-5 w-5 text-purple-700 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold">Objetivo</div>
              <p className="text-sm text-slate-800 font-semibold">{template.objective}</p>
            </div>
          </div>
        )}

        {template.description && (
          <div data-testid="task-detail-description">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Descripción</div>
            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{template.description}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 pt-1">
          <span className="inline-flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-full">
            <Share2 className="h-3 w-3" /> {teamName}
          </span>
          {template.times_used > 0 && (
            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold">
              usada {template.times_used}×
            </span>
          )}
          {(template.tags || []).map((tag) => (
            <span key={tag} className="bg-slate-100 text-slate-600 px-2 py-1 rounded-full">#{tag}</span>
          ))}
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2 pt-2 flex-wrap">
          {template.video_url && (
            <a href={template.video_url} target="_blank" rel="noreferrer" className="mr-auto">
              <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="task-detail-video">
                <Youtube className="h-4 w-4 mr-1.5" /> Ver vídeo
              </Button>
            </a>
          )}
          {hasAnim && (
            <Button
              onClick={() => setShowAnim(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="task-detail-play-anim"
            >
              <Play className="h-4 w-4 mr-1.5" /> Ver animación
            </Button>
          )}
          {canManage && (
            <Button variant="outline" onClick={onEdit} data-testid="task-detail-edit">
              <Pencil className="h-4 w-4 mr-1.5" /> Editar
            </Button>
          )}
          <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="task-detail-close">
            Cerrar
          </Button>
        </DialogFooter>

        {showAnim && (
          <TacticalBoard
            open
            onOpenChange={(o) => !o && setShowAnim(false)}
            imageOnly
            viewOnly
            initialBoardData={template.board_data}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


function TaskTemplateDialog({ template, teams, existingTags, onClose, onSaved }) {
  const isNew = !template?.id;
  const [title, setTitle] = useState(template?.title || "");
  const [description, setDescription] = useState(template?.description || "");
  const [objective, setObjective] = useState(template?.objective || "");
  const [teamId, setTeamId] = useState(template?.team_id || "__shared__");
  const [tagsText, setTagsText] = useState((template?.tags || []).join(", "));
  const [imageId, setImageId] = useState(template?.image_id || "");
  const [boardData, setBoardData] = useState(template?.board_data || null);

  const [videoUrl, setVideoUrl] = useState(template?.video_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const fileRef = React.useRef(null);
  // Unique draft id per dialog instance so multiple drafts don't share quota.
  const draftScopeId = React.useRef(
    template?.id || `__draft__${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  ).current;

  const uploadImage = async (rawFile) => {
    const ext = (rawFile.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error("Formato no permitido. Usa JPG, PNG o WebP.");
      return;
    }
    if (rawFile.size > MAX_BYTES) {
      toast.error("La imagen supera los 10 MB.");
      return;
    }
    setUploading(true);
    try {
      const file = await compressImage(rawFile);
      const fd = new FormData();
      fd.append("scope", "task_template");
      fd.append("scope_id", draftScopeId);
      fd.append("file", file);
      const r = await api.post("/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageId(r.data.id);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) uploadImage(f);
  };

  const onSave = async () => {
    if (!title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    const tags = Array.from(new Set(
      tagsText.split(",").map((t) => t.trim().toLowerCase().replace(/^#/, "")).filter(Boolean),
    ));
    const body = {
      team_id: teamId === "__shared__" ? null : teamId,
      title: title.trim(),
      description: description.trim(),
      objective: objective.trim(),
      image_id: imageId || "",
      video_url: videoUrl.trim(),
      tags,
      board_data: imageId ? boardData : null,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/task-templates", body);
        toast.success("Tarea creada");
      } else {
        await api.put(`/task-templates/${template.id}`, body);
        toast.success("Tarea actualizada");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const imgUrl = imageId ? `${BACKEND}/api/attachments/${imageId}/file` : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nueva tarea" : "Editar tarea"}</DialogTitle>
          <DialogDescription className="text-xs">
            Sube una imagen JPG/PNG/WebP (hasta 10 MB) o deja sin imagen. Etiquetas separadas por coma.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Imagen</Label>
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={onPickFile}
              className="hidden"
              data-testid="task-image-input"
            />
            {imgUrl ? (
              <div className="relative border border-slate-200 rounded-md overflow-hidden">
                <img src={imgUrl} alt="" className="w-full max-h-[260px] object-contain bg-slate-50" />
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setBoardOpen(true)}
                    className="bg-white/95"
                    data-testid="task-draw-board-again"
                  >
                    <Pencil className="h-3 w-3 mr-1" /> Pizarra
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setImageId(""); setBoardData(null); }}
                    className="bg-white/95"
                    data-testid="task-image-remove"
                  >
                    <X className="h-3 w-3 mr-1" /> Quitar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="border-2 border-dashed border-slate-300 rounded-md p-5 text-center hover:border-purple-400 hover:bg-purple-50 transition disabled:opacity-60"
                  data-testid="task-image-upload"
                >
                  <Upload className="h-6 w-6 text-slate-400 mx-auto mb-1" />
                  <div className="text-sm text-slate-600 font-bold">
                    {uploading ? "Subiendo…" : "Subir una imagen"}
                  </div>
                  <div className="text-[11px] text-slate-400">JPG · PNG · WebP · máx 10 MB</div>
                </button>
                <button
                  type="button"
                  onClick={() => setBoardOpen(true)}
                  className="border-2 border-dashed border-purple-300 rounded-md p-5 text-center hover:border-purple-500 hover:bg-purple-50 transition"
                  data-testid="task-draw-board"
                >
                  <Pencil className="h-6 w-6 text-purple-500 mx-auto mb-1" />
                  <div className="text-sm text-purple-700 font-bold">Dibujar en la pizarra</div>
                  <div className="text-[11px] text-slate-400">Campo táctico con jugadores y material</div>
                </button>
              </div>
            )}
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Título *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Ej. Rondo 4v2 con orientación"
                   data-testid="task-title" />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Objetivo</Label>
            <Input value={objective} onChange={(e) => setObjective(e.target.value)}
                   placeholder="Ej. Mejorar el primer toque orientado bajo presión"
                   data-testid="task-objective" />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Descripción</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder="Detalles, variantes, indicaciones…"
                      rows={4}
                      data-testid="task-description" />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">
              URL de vídeo (YouTube, opcional)
            </Label>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <Input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtu.be/..."
                  data-testid="task-video-url"
                />
                <p className="text-[10px] text-slate-400 mt-1">Aparecerá como QR + miniatura en el PDF de la sesión cuando se aplique esta tarea.</p>
              </div>
              <YouTubeThumbnail url={videoUrl} size={54} data-testid="task-video-preview" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                Compartir con
              </Label>
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger data-testid="task-team-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__shared__">Todo el club</SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Tag className="h-3 w-3" /> Etiquetas (coma)
              </Label>
              <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)}
                     placeholder="rondo, calentamiento, finalización"
                     data-testid="task-tags" />
              {existingTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {existingTags.slice(0, 10).map((tg) => (
                    <button
                      key={tg}
                      type="button"
                      onClick={() => {
                        const current = tagsText.split(",").map((s) => s.trim()).filter(Boolean);
                        if (!current.includes(tg)) {
                          setTagsText([...current, tg].join(", "));
                        }
                      }}
                      className="text-[10px] bg-slate-100 hover:bg-purple-100 hover:text-purple-700 text-slate-600 px-1.5 py-0.5 rounded"
                    >
                      #{tg}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="task-cancel">
            <X className="h-4 w-4 mr-1.5" /> Cancelar
          </Button>
          <Button onClick={onSave} disabled={saving || uploading}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  data-testid="task-save">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : (isNew ? "Crear tarea" : "Guardar cambios")}
          </Button>
        </DialogFooter>

        {boardOpen && (
          <TacticalBoard
            open
            onOpenChange={(o) => !o && setBoardOpen(false)}
            imageOnly
            scopeId={draftScopeId}
            initialBoardData={boardData}
            onSaved={(attId, meta) => {
              setImageId(attId);
              setBoardData(meta?.boardData || null);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
